import { create } from 'zustand';
import { API_ENDPOINTS, getApiClient } from '@respectly/api-client';

interface AddOnsError {
  code: string;
  message: string;
}

export interface AddOnListItem {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  category: string;
  billingType: 'one_time' | 'recurring';
  unitPrice: number;
  currency: string;
  minQuantity: number;
  increaseAmount?: number | null;
  increaseType?: string | null;
  affectedLimitField?: string | null;
  creditsValue?: number | null;
  creditType?: string | null;
  helcimAddOnId?: string | null;
  isSynced: boolean | null; // null = one-time (N/A), true/false for recurring
  isActive: boolean;
  displayOrder: number;
  icon?: string | null;
  metadata?: Record<string, unknown> | null;
  assignedPlanIds?: string[];
}

export interface PlanAssignmentItem {
  id: string;
  planId: string;
  planName: string;
  isEnabled: boolean;
  maxQuantityPerPeriod?: number | null;
  overriddenUnitPrice?: number | null;
}

export interface AddOnDetail extends AddOnListItem {
  planAssignments: PlanAssignmentItem[];
}

export interface SimplePlanItem {
  id: string;
  name: string;
  tierLevel: number;
}

interface AddOnFilters {
  category?: string;
  billingType?: 'one_time' | 'recurring';
  isActive?: boolean;
  search?: string;
}

interface AddOnPagination {
  skip: number;
  take: number;
  total: number;
}

export interface AddOnsAnalytics {
  catalog: {
    total: number;
    active: number;
    inactive: number;
  };
  purchases: {
    totalPurchases: number;
    activePurchases: number;
    totalRevenue: number;
    recurringActiveRevenue: number;
    recurringActiveCount: number;
    oneTimeRevenue: number;
    oneTimeCount: number;
  };
  topAddOns: Array<{
    type: string;
    name: string;
    purchases: number;
    revenue: number;
  }>;
}

export interface AddOnsState {
  addOns: AddOnListItem[];
  selectedAddOn: AddOnDetail | null;
  plans: SimplePlanItem[];
  filters: AddOnFilters;
  pagination: AddOnPagination;
  analytics: AddOnsAnalytics | null;
  isLoadingList: boolean;
  isLoadingDetail: boolean;
  isLoadingPlans: boolean;
  isLoadingAnalytics: boolean;
  isPerformingAction: boolean;
  listError: AddOnsError | null;
  actionError: AddOnsError | null;

  loadAddOns: (options?: { append?: boolean }) => Promise<void>;
  loadMoreAddOns: () => Promise<void>;
  loadAddOnDetail: (id: string) => Promise<void>;
  loadPlans: () => Promise<void>;
  loadAnalytics: () => Promise<void>;
  setFilter: <K extends keyof AddOnFilters>(key: K, value: AddOnFilters[K]) => void;
  setSearchTerm: (search: string) => void;
  clearFilters: () => void;

  createAddOn: (payload: Record<string, unknown>) => Promise<boolean>;
  updateAddOn: (id: string, payload: Record<string, unknown>) => Promise<boolean>;
  deactivateAddOn: (id: string) => Promise<boolean>;
  assignAddOnToPlan: (planId: string, addOnId: string) => Promise<boolean>;
  removeAddOnFromPlan: (planId: string, addOnId: string) => Promise<boolean>;
  updatePlanAssignments: (
    addOnId: string,
    assignments: { planId: string; isEnabled: boolean; maxQuantityPerPeriod?: number | null; overriddenUnitPrice?: number | null }[]
  ) => Promise<boolean>;

  clearError: () => void;
}

const initialState = {
  addOns: [] as AddOnListItem[],
  selectedAddOn: null as AddOnDetail | null,
  plans: [] as SimplePlanItem[],
  filters: {} as AddOnFilters,
  pagination: { skip: 0, take: 25, total: 0 } as AddOnPagination,
  analytics: null as AddOnsAnalytics | null,
  isLoadingList: false,
  isLoadingDetail: false,
  isLoadingPlans: false,
  isLoadingAnalytics: false,
  isPerformingAction: false,
  listError: null as AddOnsError | null,
  actionError: null as AddOnsError | null,
};

export const useAddOnsStore = create<AddOnsState>((set, get) => ({
  ...initialState,

  loadAddOns: async (options) => {
    set({ isLoadingList: true, listError: null });
    try {
      const { filters, pagination } = get();
      const api = getApiClient();
      const params = new URLSearchParams();
      const append = options?.append === true;
      const skip = append ? pagination.skip + pagination.take : 0;
      const take = pagination.take;

      if (filters.category) params.set('category', filters.category);
      if (filters.billingType) params.set('billingType', filters.billingType);
      if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));
      if (filters.search) params.set('search', filters.search);
      params.set('skip', String(skip));
      params.set('take', String(take));

      const path = params.toString()
        ? `${API_ENDPOINTS.SUPER_ADMIN.BILLING.ADD_ONS}?${params.toString()}`
        : API_ENDPOINTS.SUPER_ADMIN.BILLING.ADD_ONS;

      const response = await api.get<
        AddOnListItem[] |
        {
          items: AddOnListItem[];
          pagination: AddOnPagination;
        }
      >(path);

      const items = Array.isArray(response) ? response : response.items;
      const responsePagination = Array.isArray(response)
        ? {
            skip,
            take,
            total: append ? pagination.total : items.length,
          }
        : response.pagination;

      set((state) => ({
        addOns: append ? [...state.addOns, ...items] : items,
        pagination: responsePagination,
        isLoadingList: false,
      }));
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_ADDONS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load add-ons',
        },
      });
    }
  },

  loadMoreAddOns: async () => {
    const { pagination, isLoadingList } = get();
    if (isLoadingList) return;
    if (pagination.skip + pagination.take >= pagination.total) return;
    await get().loadAddOns({ append: true });
  },

  loadAddOnDetail: async (id: string) => {
    set({ isLoadingDetail: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.get<AddOnDetail>(
        API_ENDPOINTS.SUPER_ADMIN.BILLING.ADD_ON(id)
      );

      set({ selectedAddOn: response, isLoadingDetail: false });
    } catch (error) {
      set({
        isLoadingDetail: false,
        actionError: {
          code: 'LOAD_ADDON_DETAIL_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load add-on detail',
        },
      });
    }
  },

  loadPlans: async () => {
    set({ isLoadingPlans: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.get<Array<{ id: string; name: string; tierLevel: number }>>(
        API_ENDPOINTS.SUPER_ADMIN.PLANS.LIST
      );

      set({
        plans: response.map((plan) => ({
          id: plan.id,
          name: plan.name,
          tierLevel: plan.tierLevel,
        })),
        isLoadingPlans: false,
      });
    } catch (error) {
      set({
        isLoadingPlans: false,
        actionError: {
          code: 'LOAD_PLANS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load plans',
        },
      });
    }
  },

  loadAnalytics: async () => {
    set({ isLoadingAnalytics: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.get<AddOnsAnalytics>(
        API_ENDPOINTS.SUPER_ADMIN.BILLING.ADD_ONS_ANALYTICS
      );

      set({ analytics: response, isLoadingAnalytics: false });
    } catch (error) {
      set({
        isLoadingAnalytics: false,
        actionError: {
          code: 'LOAD_ADDONS_ANALYTICS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load add-ons analytics',
        },
      });
    }
  },

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
      pagination: { ...state.pagination, skip: 0 },
    }));
    get().loadAddOns();
  },

  setSearchTerm: (search) => {
    set((state) => ({
      filters: { ...state.filters, search: search.trim() || undefined },
      pagination: { ...state.pagination, skip: 0 },
    }));
    get().loadAddOns();
  },

  clearFilters: () => {
    set((state) => ({ filters: {}, pagination: { ...state.pagination, skip: 0 } }));
    get().loadAddOns();
  },

  createAddOn: async (payload) => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.BILLING.ADD_ONS, payload);
      await get().loadAddOns();
      await get().loadAnalytics();
      set({ isPerformingAction: false });
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_ADDON_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create add-on',
        },
      });
      return false;
    }
  },

  updateAddOn: async (id, payload) => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.BILLING.ADD_ON(id), payload);
      await get().loadAddOns();
      await get().loadAnalytics();
      if (get().selectedAddOn?.id === id) {
        await get().loadAddOnDetail(id);
      }
      set({ isPerformingAction: false });
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_ADDON_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update add-on',
        },
      });
      return false;
    }
  },

  deactivateAddOn: async (id) => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.BILLING.ADD_ON(id));
      await get().loadAddOns();
      await get().loadAnalytics();
      if (get().selectedAddOn?.id === id) {
        await get().loadAddOnDetail(id);
      }
      set({ isPerformingAction: false });
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DEACTIVATE_ADDON_FAILED',
          message: error instanceof Error ? error.message : 'Failed to deactivate add-on',
        },
      });
      return false;
    }
  },

  assignAddOnToPlan: async (planId, addOnId) => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.BILLING.PLAN_ADD_ONS(planId), {
        addOnIds: [addOnId],
        isEnabled: true,
      });

      if (get().selectedAddOn?.id === addOnId) {
        await get().loadAddOnDetail(addOnId);
      }

      set({ isPerformingAction: false });
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'ASSIGN_ADDON_FAILED',
          message: error instanceof Error ? error.message : 'Failed to assign add-on to plan',
        },
      });
      return false;
    }
  },

  removeAddOnFromPlan: async (planId, addOnId) => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.BILLING.PLAN_ADD_ON(planId, addOnId));

      if (get().selectedAddOn?.id === addOnId) {
        await get().loadAddOnDetail(addOnId);
      }

      set({ isPerformingAction: false });
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'REMOVE_ADDON_FAILED',
          message: error instanceof Error ? error.message : 'Failed to remove add-on from plan',
        },
      });
      return false;
    }
  },

  updatePlanAssignments: async (addOnId, assignments) => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.BILLING.ADD_ON_PLAN_ASSIGNMENTS(addOnId), {
        assignments,
      });
      await get().loadAddOns();
      if (get().selectedAddOn?.id === addOnId) {
        await get().loadAddOnDetail(addOnId);
      }
      set({ isPerformingAction: false });
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_PLAN_ASSIGNMENTS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update plan assignments',
        },
      });
      return false;
    }
  },

  clearError: () => {
    set({ listError: null, actionError: null });
  },
}));
